import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CronableTask,
  ScheduledTask,
  ScheduledTaskPayload,
} from "../Models/scheduledTask";
import {
  createScheduledTask,
  deleteMyScheduledTask,
  deleteScheduledTask,
  getAllScheduledTasks,
  getAvailableCronableTasks,
  getMyScheduledTasks,
  runScheduledTaskNow,
  toggleMyScheduledTask,
  toggleScheduledTask,
  updateScheduledTask,
} from "../Api/scheduledTask";

const SCHEDULED_TASKS_KEY = ["scheduledTasks"];
const MY_SCHEDULED_TASKS_KEY = ["myScheduledTasks"];

export const useAvailableCronableTasks = () => {
  return useQuery<CronableTask[], Error>({
    queryKey: ["cronableTasks"],
    queryFn: () => getAvailableCronableTasks(),
  });
};

export const useScheduledTasks = () => {
  return useQuery<ScheduledTask[], Error>({
    queryKey: SCHEDULED_TASKS_KEY,
    queryFn: () => getAllScheduledTasks(),
  });
};

export const useCreateScheduledTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ScheduledTaskPayload) => createScheduledTask(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCHEDULED_TASKS_KEY });
    },
  });
};

export const useUpdateScheduledTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: number; data: ScheduledTaskPayload }) =>
      updateScheduledTask(variables.id, variables.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCHEDULED_TASKS_KEY });
    },
  });
};

export const useDeleteScheduledTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteScheduledTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCHEDULED_TASKS_KEY });
    },
  });
};

export const useToggleScheduledTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: number; enabled: boolean }) =>
      toggleScheduledTask(variables.id, variables.enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCHEDULED_TASKS_KEY });
    },
  });
};

export const useRunScheduledTaskNow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => runScheduledTaskNow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCHEDULED_TASKS_KEY });
    },
  });
};

export const useMyScheduledTasks = () => {
  return useQuery<ScheduledTask[], Error>({
    queryKey: MY_SCHEDULED_TASKS_KEY,
    queryFn: () => getMyScheduledTasks(),
  });
};

export const useToggleMyScheduledTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: number; enabled: boolean }) =>
      toggleMyScheduledTask(variables.id, variables.enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MY_SCHEDULED_TASKS_KEY });
    },
  });
};

export const useDeleteMyScheduledTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteMyScheduledTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MY_SCHEDULED_TASKS_KEY });
    },
  });
};
